import React, { type JSX } from "react";
import { getTimezoneAbbreviation } from "../../utils/formatDate";
import { useConversationList } from "./conversations/useConversationList";
import ConversationFiltersPanel from "./conversations/ConversationFiltersPanel";
import ConversationsSummaryBar from "./conversations/ConversationsSummaryBar";
import ConversationsTable from "./conversations/ConversationsTable";
import ConversationsPagination from "./conversations/ConversationsPagination";

export default function ConversationsView(): JSX.Element {
  const {
    conversations,
    pagination,
    loading,
    error,
    filters,
    draft,
    handleFilterChange,
    handleDraftChange,
    handlePageChange,
    clearFilters,
  } = useConversationList();

  // Only the very first load takes over the page. Returning a spinner from
  // here on every load unmounted the filter panel, so the inputs were rebuilt
  // on each keystroke and focus was lost after a single character. Later
  // refetches leave the panel and the previous rows in place.
  const firstLoad = loading && pagination === null;

  return (
    <div className="space-y-6">
      <ConversationFiltersPanel
        filters={filters}
        draft={draft}
        onFilterChange={handleFilterChange}
        onDraftChange={handleDraftChange}
        onClearFilters={clearFilters}
      />

      <ConversationsSummaryBar
        pagination={pagination}
        error={error}
        pageSize={filters.page_size}
        hidingZeroTurn={filters.hasTurns === true}
        onFilterChange={handleFilterChange}
      />

      <p className="text-xs text-gray-500 mb-1">All times shown in {getTimezoneAbbreviation()}</p>
      {firstLoad ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className={loading ? "opacity-50 transition-opacity" : ""}>
          <ConversationsTable conversations={conversations} loading={loading} />
        </div>
      )}

      {pagination && pagination.total_pages > 1 && (
        <ConversationsPagination pagination={pagination} onPageChange={handlePageChange} />
      )}
    </div>
  );
}
